import {useMemo, useState} from 'react';

import {Subscriptions} from '~/modules/api';
import {useGetConfigQuery, useGetDiscountCodesQuery} from '~/store/rtkQuery';

import {EditDiscountCodeModal} from './EditDiscountCodeModal';
import {CreateDiscountCodeModal} from './CreateDiscountCodeModal';
import {DiscountCodesTrackingModal} from './DiscountCodesTrackingModal';

type FilterType = 'all' | 'active' | 'inactive';

// Helper function to format date
const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString; // Fallback to original string if parsing fails
  }
};

export const DiscountCodes = () => {
  const {data, error, refetch} = useGetDiscountCodesQuery();
  const {data: config, error: configError} = useGetConfigQuery();

  const discountTypes = useMemo(() => {
    return Object.entries(
      config?.sysAdminConfig?.purchasely?.discountTypes ?? {},
    ).map(([key, value]) => {
      return {
        discountType: key,
        audience: value.audience,
        title: value.title,
      };
    });
  }, [config]);

  const [editCode, setEditCode] = useState<Subscriptions.DiscountCode | null>(
    null,
  );
  const [showCreate, setShowCreate] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>('all');

  // Filter codes based on filterType state
  const filteredCodes = useMemo(() => {
    if (!data?.discountCodes) return [];
    let filtered = [];
    switch (filterType) {
      case 'active':
        filtered = data.discountCodes.filter(code => code.active);
        break;
      case 'inactive':
        filtered = data.discountCodes.filter(code => !code.active);
        break;
      default:
        filtered = data.discountCodes;
    }

    // Sort by createdAt descending (newest first) - create new array to avoid modifying read-only array
    return [...filtered].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data?.discountCodes, filterType]);

  if (error || configError) {
    return (
      <div className="p-5">
        <h1 className="text-2xl font-semibold">Discount Codes</h1>
        <p className="mt-4 text-red-600">
          {error ? error.message : configError?.message}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="p-5">
        <h1 className="text-2xl font-semibold">Discount Codes</h1>

        {/* Results count */}
        <div className="mt-2 text-sm text-gray-600">
          Showing {filteredCodes.length} of {data?.discountCodes?.length || 0}{' '}
          codes
        </div>

        <div className="mt-4 flex flex-row justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium mr-2">Filter:</span>
            <div className="join">
              <button
                className={`btn btn-sm join-item ${filterType === 'all' ? 'btn-active' : ''}`}
                onClick={() => setFilterType('all')}>
                All
              </button>
              <button
                className={`btn btn-sm join-item ${filterType === 'active' ? 'btn-active' : ''}`}
                onClick={() => setFilterType('active')}>
                Active
              </button>
              <button
                className={`btn btn-sm join-item ${filterType === 'inactive' ? 'btn-active' : ''}`}
                onClick={() => setFilterType('inactive')}>
                Inactive
              </button>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <button
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}>
              Create
            </button>
            <button
              className="btn btn-accent"
              onClick={() => setShowTracking(true)}>
              View tracking
            </button>
          </div>
        </div>
        {!!data && (
          <div className="mt-4">
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Audience</th>
                  <th>Discount Type</th>
                  <th>Active</th>
                  <th>Created At</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map(code => {
                  const discountType = discountTypes.find(
                    type => type.discountType === code.discountType,
                  );
                  if (!discountType) return null;
                  return (
                    <tr key={code.id}>
                      <td>{code.code}</td>
                      <td>{discountType.audience ?? 'unknown'}</td>
                      <td>{discountType.title ?? code.discountType}</td>
                      <td>{code.active ? 'yes' : 'no'}</td>
                      <td>{formatDate(code.createdAt)}</td>
                      <td>
                        <button
                          className="btn"
                          onClick={() => setEditCode(code)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!!editCode && (
        <EditDiscountCodeModal
          code={editCode}
          onCancel={() => setEditCode(null)}
          onComplete={() => {
            setEditCode(null);
            refetch();
          }}
        />
      )}

      {showCreate && (
        <CreateDiscountCodeModal
          discountTypes={discountTypes}
          onCancel={() => setShowCreate(false)}
          onComplete={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {showTracking && (
        <DiscountCodesTrackingModal onCancel={() => setShowTracking(false)} />
      )}
    </>
  );
};
